import React, { useState } from 'react';
import { MedicalOpinionCloseReason } from '../../types/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { getTextDirection } from '../../utils/textDirection';
import { useMedicalOpinionCloseReasons } from '../../hooks/useMedicalOpinionOptions';

interface CloseMedicalOpinionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (closeReason: MedicalOpinionCloseReason, notes: string) => Promise<void>;
    isSaving?: boolean;
    serverError?: string | null;
}

export const CloseMedicalOpinionModal: React.FC<CloseMedicalOpinionModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isSaving = false,
    serverError = null,
}) => {
    const {
        options: closeReasonOptions,
        isLoading: isLoadingCloseReasons,
        isError: closeReasonsFailed,
    } = useMedicalOpinionCloseReasons();

    const [closeReason, setCloseReason] = useState<MedicalOpinionCloseReason | ''>('');
    const [notes, setNotes] = useState('');
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    // Which reason needs free text comes off the option itself, so the rule lives in one
    // place - the backend enforces the same thing on submit.
    const selectedReason = closeReasonOptions.find((opt) => opt.value === closeReason);
    const notesRequired = selectedReason?.requires_notes ?? false;

    const handleConfirm = async () => {
        if (!closeReason) {
            setError('Please select a close reason');
            return;
        }
        if (notesRequired && !notes.trim()) {
            setError(`Notes are required when the reason is ${selectedReason?.label ?? closeReason}`);
            return;
        }
        setError(null);
        // The parent closes this modal (and the approval modal) on success.
        await onConfirm(closeReason, notes);
    };

    const handleCancel = () => {
        setCloseReason('');
        setNotes('');
        setError(null);
        onClose();
    };

    const shownError = error || serverError;

    return (
        <ModalWrapper modalId="CloseMedicalOpinionModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Close Request"
            isLoading={isSaving}
        >
            <div className="space-y-4 mb-6">
                <p className="text-sm text-[#366A75]">
                    The patient is notified by email and push. A closed request cannot be reopened.
                </p>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Close Reason <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={closeReason}
                        onChange={(e) => setCloseReason(e.target.value as MedicalOpinionCloseReason)}
                        className={POPUP_STYLES.inputBordered}
                        disabled={isSaving || isLoadingCloseReasons || closeReasonsFailed}
                    >
                        <option value="">
                            {isLoadingCloseReasons
                                ? 'Loading close reasons...'
                                : 'Select a close reason'}
                        </option>
                        {closeReasonOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    {closeReasonsFailed && (
                        <p className="mt-1 text-xs text-red-600">
                            Could not load the close reasons. Please reload the page.
                        </p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Notes {notesRequired && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        // Matches how the approval modal renders this text back as Close Notes.
                        dir={getTextDirection(notes)}
                        className={`${POPUP_STYLES.inputBordered} resize-none`}
                        disabled={isSaving}
                    />
                    {/* The patient never sees this text - only the reason label reaches them. */}
                    <p className="mt-1 text-xs text-gray-500">
                        Internal only. The patient is told the reason by its label, not this text.
                    </p>
                </div>
            </div>

            {shownError && (
                <div className="mb-4 p-3 bg-red-100 border-[1px] border-red-400 text-red-700 rounded-lg text-sm">
                    {shownError}
                </div>
            )}

            <div className="flex justify-center gap-4">
                <button onClick={handleConfirm} disabled={isSaving} className={POPUP_STYLES.button}>
                    {isSaving ? 'Closing...' : 'Confirm Close'}
                </button>
                <button onClick={handleCancel} disabled={isSaving} className={POPUP_STYLES.button}>
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
